import React, { useMemo, useState } from "react";
import { PlanPlaceableItem, PlanDragItem } from "./planTypes";

interface PlanSidebarProps {
  units: PlanPlaceableItem[];
  attributes: PlanPlaceableItem[];
  /** Id (`${targetType}:${id}`) currently being drawn as a contour, if any. */
  activeContourKey: string | null;
  onStartContour: (item: PlanPlaceableItem) => void;
  onLocate: (item: PlanPlaceableItem) => void;
}

const itemKey = (i: PlanPlaceableItem) => `${i.targetType}:${i.id}`;

/** A draggable list item for a unit or attribute. */
const SidebarItem: React.FC<{
  item: PlanPlaceableItem;
  isDrawing: boolean;
  onStartContour: (item: PlanPlaceableItem) => void;
  onLocate: (item: PlanPlaceableItem) => void;
}> = ({ item, isDrawing, onStartContour, onLocate }) => {
  const isPlaced = item.hasPin || item.hasContour;

  const handleDragStart = (e: React.DragEvent) => {
    const payload: PlanDragItem = {
      targetType: item.targetType,
      id: item.id,
      name: item.name,
      color: item.color,
    };
    e.dataTransfer.setData("application/json", JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className={`group flex items-center gap-2 rounded-lg border p-2 cursor-grab active:cursor-grabbing transition-colors ${
        isDrawing
          ? "border-brick-primary bg-brick-primary/10"
          : "border-neutral-primary/15 bg-white hover:border-moss-primary hover:bg-brick-secondary-fine"
      }`}
      title="Sleep naar de plattegrond om te plaatsen"
    >
      <span
        className="h-3 w-3 shrink-0 rounded-full"
        style={{ backgroundColor: item.color }}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-neutral-primary">{item.name}</div>
        {item.subtitle && (
          <div className="truncate text-xs text-neutral-secondary-mid">{item.subtitle}</div>
        )}
      </div>

      <div className="flex items-center gap-1">
        {item.hasPin && (
          <span title="Pin geplaatst" className="text-xs">📍</span>
        )}
        {item.hasContour && (
          <span title="Contour getekend" className="text-xs">⬠</span>
        )}
        {isPlaced && (
          <button
            type="button"
            onClick={() => onLocate(item)}
            title="Toon op plattegrond"
            className="rounded px-1 text-xs text-moss-primary opacity-0 transition-opacity group-hover:opacity-100 hover:underline"
          >
            tonen
          </button>
        )}
        <button
          type="button"
          onClick={() => onStartContour(item)}
          title="Teken een contour voor dit item"
          className={`rounded border px-1.5 py-0.5 text-xs transition-colors ${
            isDrawing
              ? "border-brick-primary bg-brick-primary text-brick-secondary-fine"
              : "border-neutral-primary/20 text-neutral-secondary-mid hover:border-brick-primary hover:text-brick-primary"
          }`}
        >
          contour
        </button>
      </div>
    </div>
  );
};

const Section: React.FC<{
  title: string;
  count: number;
  items: PlanPlaceableItem[];
  activeContourKey: string | null;
  onStartContour: (item: PlanPlaceableItem) => void;
  onLocate: (item: PlanPlaceableItem) => void;
}> = ({ title, count, items, activeContourKey, onStartContour, onLocate }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-sm font-semibold text-neutral-primary"
      >
        <span>
          {title} <span className="text-neutral-secondary-mid">({count})</span>
        </span>
        <span className="text-neutral-secondary-mid">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="space-y-1.5">
          {items.length === 0 ? (
            <p className="text-xs text-neutral-secondary-mid">Geen items</p>
          ) : (
            items.map((item) => (
              <SidebarItem
                key={itemKey(item)}
                item={item}
                isDrawing={activeContourKey === itemKey(item)}
                onStartContour={onStartContour}
                onLocate={onLocate}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export const PlanSidebar: React.FC<PlanSidebarProps> = ({
  units,
  attributes,
  activeContourKey,
  onStartContour,
  onLocate,
}) => {
  const [query, setQuery] = useState("");

  const filter = (items: PlanPlaceableItem[]) => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) => i.name.toLowerCase().includes(q) || i.subtitle?.toLowerCase().includes(q),
    );
  };

  const filteredUnits = useMemo(() => filter(units), [units, query]);
  const filteredAttributes = useMemo(() => filter(attributes), [attributes, query]);

  return (
    <div className="flex h-full w-72 shrink-0 flex-col gap-4 overflow-y-auto rounded-lg border border-neutral-primary/15 bg-brick-secondary-fine p-3">
      <div>
        <h3 className="text-sm font-semibold text-neutral-primary">Plaatsen op plattegrond</h3>
        <p className="text-xs text-neutral-secondary-mid">
          Sleep een item op de plattegrond, of klik “contour” om de vorm te tekenen.
        </p>
      </div>

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Zoeken…"
        className="w-full rounded-lg border border-neutral-primary/20 bg-white px-3 py-1.5 text-sm text-neutral-primary outline-none focus:border-moss-primary"
      />

      <Section
        title="Units"
        count={units.length}
        items={filteredUnits}
        activeContourKey={activeContourKey}
        onStartContour={onStartContour}
        onLocate={onLocate}
      />
      <Section
        title="Gebouwattributen"
        count={attributes.length}
        items={filteredAttributes}
        activeContourKey={activeContourKey}
        onStartContour={onStartContour}
        onLocate={onLocate}
      />
    </div>
  );
};
